"use client";

import { useEffect, useId, useRef, useState } from "react";
import { Button } from "@astryxdesign/core/Button";
import { ResearchIcon } from "@/components/icons/research-icon";
import type { LandingNavLink } from "@/components/landing/header-view";
import type { LandingAccountUser } from "@/components/landing/landing-account-menu";
import { LandingPhoneContact } from "@/components/landing/landing-phone-contact";
import { resolveLandingNavHref } from "@/lib/landing/nav-href";
import {
  landingTextProps,
  styledTextString,
  styledTextStyle,
} from "@/lib/landing/puck/styled-text";

type LandingHeaderNavProps = {
  links: LandingNavLink[];
  /** Вертикальный список для мобильной панели */
  variant?: "desktop" | "mobile";
  onNavigate?: () => void;
};

export function LandingHeaderNav({
  links,
  variant = "desktop",
  onNavigate,
}: LandingHeaderNavProps) {
  if (variant === "mobile") {
    return (
      <nav className="flex w-full flex-col gap-1 [font-family:var(--landing-font-family,Arial,Helvetica,sans-serif)]">
        {links.map((link) =>
          link.children && link.children.length > 0 ? (
            <MobileNavGroup
              key={styledTextString(link.label)}
              link={link}
              onNavigate={onNavigate}
            />
          ) : (
            <a
              key={`${link.href}-${styledTextString(link.label)}`}
              href={resolveLandingNavHref(link.href)}
              className="rounded-lg px-3 py-2.5 text-[length:var(--landing-nav-font-size,1rem)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))] no-underline hover:bg-[var(--color-surface-muted)] hover:font-bold hover:text-[var(--landing-nav-color-active,var(--color-text-accent))]"
              onClick={onNavigate}
            >
              {styledTextString(link.label)}
            </a>
          ),
        )}
      </nav>
    );
  }

  return (
    <nav className="flex w-max max-w-full items-baseline gap-3 [font-family:var(--landing-font-family,Arial,Helvetica,sans-serif)] lg:gap-5">
      {links.map((link) =>
        link.children && link.children.length > 0 ? (
          <LandingHeaderNavDropdown key={styledTextString(link.label)} link={link} />
        ) : (
          <a
            key={`${link.href}-${styledTextString(link.label)}`}
            href={resolveLandingNavHref(link.href)}
            className="text-[length:var(--landing-nav-font-size,0.875rem)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))] no-underline transition-colors hover:font-bold hover:text-[var(--landing-nav-color-active,var(--color-text-accent))]"
            style={styledTextStyle(link.label)}
          >
            {styledTextString(link.label)}
          </a>
        ),
      )}
    </nav>
  );
}

function ChevronGlyph({ open }: { open?: boolean }) {
  return (
    <svg
      viewBox="0 0 12 12"
      width="11"
      height="11"
      aria-hidden
      className={`block transition-transform duration-150 ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 4.5 6 7.5 9 4.5" />
    </svg>
  );
}

function LandingHeaderNavDropdown({ link }: { link: LandingNavLink }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = () => setOpen((v) => !v);

  return (
    <div ref={rootRef} className="relative inline-block shrink-0">
      <button
        type="button"
        className="inline-flex items-center gap-1 border-0 bg-transparent p-0 text-[length:var(--landing-nav-font-size,0.875rem)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))] transition-colors hover:font-bold hover:text-[var(--landing-nav-color-active,var(--color-text-accent))]"
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        onClick={toggle}
        style={styledTextStyle(link.label)}
      >
        {styledTextString(link.label)}
        <span
          aria-hidden
          className="text-[var(--landing-nav-color-active,var(--color-text-accent))]"
        >
          <ChevronGlyph open={open} />
        </span>
      </button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          className="absolute left-1/2 top-full z-50 mt-1.5 w-max min-w-[10rem] -translate-x-1/2 overflow-hidden border border-border bg-[var(--color-background-body)] py-1 shadow-lg [border-radius:var(--landing-submenu-radius,20px)]"
        >
          {link.children!.map((child) => (
            <a
              key={`${child.href}-${styledTextString(child.label)}`}
              href={resolveLandingNavHref(child.href)}
              role="menuitem"
              className="block px-3.5 py-2 text-[length:var(--landing-nav-font-size,0.875rem)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))] no-underline transition-colors hover:bg-[var(--color-surface-muted)] hover:font-bold hover:text-[var(--landing-nav-color-active,var(--color-text-accent))]"
              style={styledTextStyle(child.label)}
              onClick={() => setOpen(false)}
            >
              {styledTextString(child.label)}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MobileNavGroup({
  link,
  onNavigate,
}: {
  link: LandingNavLink;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className="flex w-full items-center justify-between rounded-lg border-0 bg-transparent px-3 py-2.5 text-left text-[length:var(--landing-nav-font-size,1rem)] font-normal text-[var(--landing-nav-color,var(--color-text-primary))]"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span {...landingTextProps(link.label)} />
        <span className="text-[var(--color-text-accent)]">
          <ChevronGlyph open={open} />
        </span>
      </button>
      {open ? (
        <div className="mb-1 ml-2 flex flex-col border-l border-border pl-2">
          {link.children!.map((child) => (
            <a
              key={`${child.href}-${styledTextString(child.label)}`}
              href={resolveLandingNavHref(child.href)}
              className="rounded-lg px-3 py-2 text-[length:var(--landing-nav-font-size,0.875rem)] font-normal text-[var(--landing-nav-color-muted,var(--color-text-secondary))] no-underline hover:bg-[var(--color-surface-muted)] hover:font-bold hover:text-[var(--landing-nav-color-active,var(--color-text-accent))]"
              style={styledTextStyle(child.label)}
              onClick={onNavigate}
            >
              {styledTextString(child.label)}
            </a>
          ))}
        </div>
      ) : null}
    </div>
  );
}

type LandingHeaderMobileMenuProps = {
  links: LandingNavLink[];
  primaryLabel: unknown;
  primaryHref: string;
  isLoggedIn: boolean;
  isReturning: boolean;
  user?: LandingAccountUser | null;
};

/** Бургер + выезжающая панель; живёт отдельно, чтобы шапка оставалась RSC. */
export function LandingHeaderMobileMenu({
  links,
  primaryLabel,
  primaryHref,
  isLoggedIn,
  isReturning,
  user = null,
}: LandingHeaderMobileMenuProps) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-[var(--color-text-primary)] lg:hidden"
        aria-label={open ? "Закрыть меню" : "Открыть меню"}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ResearchIcon name={open ? "close" : "menu"} size="md" />
      </button>

      {open ? (
        <div
          className="absolute inset-x-0 top-full z-50 border-b border-border bg-[var(--color-background-body)] shadow-lg lg:hidden"
          id="landing-mobile-nav"
        >
          <div className="mx-auto flex w-full max-w-[1120px] flex-col gap-3 px-4 py-4 sm:px-6">
            <LandingHeaderNav
              links={links}
              variant="mobile"
              onNavigate={close}
            />
            <div className="flex flex-col gap-2 border-t border-border pt-3 lg:hidden">
              {isLoggedIn ? (
                <>
                  <a href="/dashboard" onClick={close}>
                    <Button label="Кабинет" variant="primary" className="w-full" />
                  </a>
                  <a href="/settings" onClick={close}>
                    <Button label="Настройки" variant="secondary" className="w-full" />
                  </a>
                  {user?.isAdmin ? (
                    <a href="/admin" onClick={close}>
                      <Button label="Админка" variant="secondary" className="w-full" />
                    </a>
                  ) : null}
                </>
              ) : (
                <>
                  <a href="/login" onClick={close}>
                    <Button
                      label="Войти"
                      variant="secondary"
                      className="w-full"
                    />
                  </a>
                  {!isReturning ? (
                    <a href={primaryHref} onClick={close}>
                      <Button
                        label={styledTextString(primaryLabel)}
                        variant="primary"
                        className="w-full"
                        style={styledTextStyle(primaryLabel)}
                      />
                    </a>
                  ) : null}
                </>
              )}
              <LandingPhoneContact
                placement="below"
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-border bg-transparent px-3 py-2 text-sm font-medium text-primary"
                showNumber
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
